// b17: personers enheter blir aldrig fynd. Telefoner, klockor och datorer (och hörlurar och
// träningsklockor) hör till en person, inte till platsen, och ska inte till molnet. Navet hoppar
// över dem i upptäckten, och molnet sparar dem inte om ett äldre nav skickar dem.
//
// En enhet är personlig när:
//   - den kommer från en integration som bara finns för personer och deras enheter:
//     mobilappen (mobile_app), iCloud, platsdelning (Life360, OwnTracks och liknande) och
//     Bluetooth-spårning av telefoner (private_ble_device);
//   - den är en klient som en router spårar (en device_tracker från en integration för nätverk,
//     till exempel UniFi eller FRITZ!Box), eftersom det oftast är en telefon eller en dator;
//   - modellen eller tillverkaren säger telefon, klocka eller dator, till exempel iPhone, Pixel,
//     OnePlus, OPPO, Apple Watch eller MacBook.
// Regeln läser bara integrationen, tillverkaren och modellen, aldrig enhetens namn.
//
// b18 (BK42, PL6): surfplattor är inte personliga. En surfplatta, en skärm i kioskläge (Fully
// Kiosk och browser_mod) och en pekskärm (Control4 och liknande) sitter fast på platsen och
// styr den. De blir "fasta skärmar": bara modellen och klassen skickas, aldrig namnet, en
// position, en device_tracker eller värden från appens sensorer. Undantaget är surfplattor från
// integrationer för spårning (iCloud, Life360 och liknande), som räknas som personens. Telefoner,
// klockor och datorer tas fortfarande aldrig med, också i kioskläge.
//
// b20: fler surfplattor (Samsung SM-P, Amazon Fire med id:t KF…, Xiaomi och Redmi Pad, och "Tab"
// eller "Pad" som ett eget ord), och en surfplatta går före telefonens märke: en Redmi Pad var
// förut en Redmi-telefon, och hoppades över. En enhet från mobilappen vars entiteter säger att det
// är en surfplatta (enhetstypen eller skärmstorleken) är också en fast skärm. Varför en enhet
// hoppades över (personligKlass) visas bara lokalt i navet.
//
// b20-1: Lenovos surfplattor känns igen på båda formerna av modellbeteckningen: den gamla med
// bindestreck (TB-X606F) och den nya utan, TB följt av tre siffror och 1–3 bokstäver (TB373FU,
// TB370FU, TB331FC). Bara när tillverkaren är Lenovo (oavsett versaler: appen för Android skickar
// ofta LENOVO), så att regeln inte träffar en annan tillverkares modell med TB.

/** Integrationer i Home Assistant som bara har personers enheter. */
export const PERSONLIGA_INTEGRATIONER: readonly string[] = [
  "mobile_app",
  "ios",
  "icloud",
  "life360",
  "owntracks",
  "gpslogger",
  "geofency",
  "locative",
  "traccar",
  "traccar_server",
  "private_ble_device",
  "google_maps",
  "fitbit",
  "withings",
  "garmin_connect",
];

/** b18: integrationerna för personer där en surfplatta ändå inte blir en fast skärm. */
const SPARNINGSINTEGRATIONER: readonly string[] = PERSONLIGA_INTEGRATIONER.filter(
  (i) => i !== "mobile_app" && i !== "ios",
);

/** Integrationer för nätverk, vars device_tracker är en klient på nätet. */
export const NATVERKSINTEGRATIONER: readonly string[] = [
  "unifi",
  "fritz",
  "asuswrt",
  "netgear",
  "tplink_omada",
  "mikrotik",
  "keenetic_ndms2",
  "luci",
  "ubus",
  "ddwrt",
  "huawei_lte",
  "nmap_tracker",
  "ping",
  "bluetooth_tracker",
  "bluetooth_le_tracker",
];

/** b18: integrationerna för skärmar i kioskläge: alltid en fast skärm. */
export const KIOSKINTEGRATIONER: readonly string[] = ["fully_kiosk", "browser_mod"];

type Personlig = "telefon" | "klocka" | "dator" | "hörlurar";

/** Modeller och tillverkare som är telefoner, klockor, datorer eller hörlurar, med sorten (b20). */
const MODELLER_MED_SORT: readonly [RegExp, Personlig][] = [
  // Apple, också modellernas id (iPhone15,2, Watch6,2, MacBookPro18,1, Mac14,2).
  [/\bi(phone|pod)/i, "telefon"],
  [/\bapple watch\b/i, "klocka"],
  [/^watch\d+,\d+$/i, "klocka"],
  [/\bmacbook/i, "dator"],
  [/\bimac\b/i, "dator"],
  [/\bmac ?(mini|pro|studio)\b/i, "dator"],
  [/^mac\d+,\d+$/i, "dator"],
  [/\bairpods\b/i, "hörlurar"],
  // Google, Samsung och de andra som gör telefoner. b18: inte deras surfplattor (SURFPLATTOR).
  [/\bpixel ?(watch)/i, "klocka"],
  [/\bpixel ?(buds)/i, "hörlurar"],
  [/\bpixel ?(\d|fold|pro)/i, "telefon"],
  [/\bgalaxy ?(watch)/i, "klocka"],
  [/\bgalaxy ?(buds)/i, "hörlurar"],
  [/\bgalaxy ?(s\d|a\d|z|note|fold|flip)/i, "telefon"],
  [/^sm-[a-oq-su-wyz]\d{3}/i, "telefon"],
  [/\b(redmi|poco|xperia|fairphone)\b/i, "telefon"],
  [/\bmoto ?[a-z]?\d/i, "telefon"],
  // Datorer.
  [/\b(thinkpad|chromebook|elitebook|latitude|ideapad|zenbook|vivobook)\b/i, "dator"],
  [/\bsurface ?(pro|laptop|book|go)\b/i, "dator"],
  [/\b(laptop|notebook)\b/i, "dator"],
  [/\bsmartphone\b/i, "telefon"],
  [/\bsmartwatch\b/i, "klocka"],
  [/\bhass\.agent\b/i, "dator"],
  // Klockor och träningsklockor.
  [/\bfitbit\b/i, "klocka"],
  [/\b(fenix|forerunner|vivoactive|venu|epix|instinct)\b/i, "klocka"],
];
const MODELLER: readonly RegExp[] = MODELLER_MED_SORT.map(([m]) => m);
const SORT_FOR_MODELL: readonly Personlig[] = MODELLER_MED_SORT.map(([, s]) => s);

/**
 * b18: surfplattor, också modellernas id (iPad13,1, Samsungs SM-T och SM-X; Lenovos TB- och TB373FU
 * i arLenovoPlatta, b20-1). b20:
 * Samsungs SM-P, Amazons Fire med modellernas id (KFTRWI, KFMAWI …), Xiaomi och Redmi Pad, och
 * modeller med "Tab" eller "Pad" som ett eget ord.
 */
const SURFPLATTOR: readonly RegExp[] = [
  /\bipad/i,
  /\bgalaxy ?tab\b/i,
  /^sm-[ptx]\d{3}/i,
  /\b(lenovo|xiaomi|redmi|oneplus|nokia|amazon|honor|realme|huawei|tcl) ?(tab|pad)\b/i,
  /\bpixel ?tablet\b/i,
  /\bamazon\b.*\bfire\b/i,
  /\bfire ?(hd|max)\b/i,
  /^kf[a-z]{3,6}$/i,
  /\b(mediapad|matepad|tablet|surfplatta)\b/i,
];

/**
 * b20: "Tab" eller "Pad" som ett eget ord (Lenovo Tab P11, Redmi Pad SE), bara från integrationerna
 * där en surfplatta finns: mobilappen, kioskläget och spårningarna. En knappsats via Zigbee är det
 * inte.
 */
const TAB_ELLER_PAD = /\b(tab|pad)\b/i;
const PLATTINTEGRATIONER = ["mobile_app", "ios", "fully_kiosk", "browser_mod"];

/**
 * b20-1: Lenovos surfplattor: TB-X606F (med bindestreck) och TB373FU (tre siffror och 1–3
 * bokstäver, utan), bara när tillverkaren är Lenovo.
 */
const LENOVO = /^lenovo\b/i;
const LENOVO_PLATTA = /\btb(?:-[a-z0-9]{3,8}|\d{3}[a-z]{1,3})\b/i;

/** b20-1: en surfplatta från Lenovo, efter tillverkaren och modellbeteckningen. */
export function arLenovoPlatta(e: Pick<Personuppgift, "tillverkare" | "modell">): boolean {
  const modell = e.modell?.trim() ?? "";
  const lenovo = LENOVO.test(e.tillverkare?.trim() ?? "") || LENOVO.test(modell);
  return lenovo && LENOVO_PLATTA.test(modell);
}

/** b18: pekskärmar som sitter på väggen, och Control4:s skärmar. */
const PEKSKARMAR: readonly RegExp[] = [/\b(touch ?screen|touch ?panel|wall ?panel|kiosk)\b/i];
const CONTROL4_SKARM = /\b(touch|screen|panel|display|t[3-5])\b/i;

/** Tillverkare som bara gör telefoner (och klockor och hörlurar till dem). */
const TILLVERKARE = /^(oneplus|oppo|realme|vivo|fairphone|hmd global|nothing|honor)\b/i;

export interface Personuppgift {
  integration?: string | null;
  tillverkare?: string | null;
  modell?: string | null;
  /** Enheten har en device_tracker (en position eller närvaro på nätet). */
  sparas?: boolean;
  /**
   * b20: enhetens entiteter säger att det är en surfplatta (en sensor för enhetstypen eller
   * skärmstorleken, se surfplattaEnligtEntitet), till exempel från mobilappen.
   */
  surfplatta?: boolean;
}

/**
 * b20: säger en entitet att enheten är en surfplatta? En sensor för enhetstypen (device_type,
 * form_factor) med värdet tablet, eller för skärmstorleken (screen_size, screen_diagonal) med minst
 * 7 tum. Bara entitetens id och värde läses, aldrig namnet.
 */
export function surfplattaEnligtEntitet(entitet: string, varde: string | null): boolean {
  if (varde === null) return false;
  if (/_(device_type|form_factor|enhetstyp)$/.test(entitet))
    return /\b(tablet|surfplatta)\b/i.test(varde);
  if (/_(screen_size|screen_diagonal|skarmstorlek)$/.test(entitet)) {
    const tum = Number.parseFloat(varde.replace(",", "."));
    return Number.isFinite(tum) && tum >= 7 && tum <= 20;
  }
  return false;
}

/** b20: entiteterna vars värde surfplattaEnligtEntitet läser. */
export const SURFPLATTEENTITET =
  /_(device_type|form_factor|enhetstyp|screen_size|screen_diagonal|skarmstorlek)$/;

const textFor = (e: Personuppgift) =>
  [e.tillverkare, e.modell].filter((x): x is string => !!x).join(" ");

/**
 * Säger modellen eller tillverkaren telefon, klocka, dator eller hörlurar? b20: en surfplatta går
 * före, så att till exempel en Redmi Pad inte räknas som en Redmi-telefon.
 */
function arPersonligModell(e: Personuppgift): boolean {
  return personligModell(e) !== null;
}

/** b20: vilken sorts personlig modell, för navets lokala status, eller null. */
function personligModell(e: Personuppgift): Personlig | null {
  if (arSurfplatta(e)) return null;
  if (e.tillverkare && TILLVERKARE.test(e.tillverkare.trim())) return "telefon";
  const text = textFor(e);
  if (text === "") return null;
  const traffar = (m: RegExp) => m.test(text) || (e.modell ? m.test(e.modell) : false);
  const i = MODELLER.findIndex(traffar);
  if (i < 0) return null;
  return SORT_FOR_MODELL[i] ?? "telefon";
}

function arSurfplatta(e: Personuppgift): boolean {
  if (e.surfplatta) return true;
  if (arLenovoPlatta(e)) return true;
  const text = textFor(e);
  if (text === "") return false;
  if (SURFPLATTOR.some((m) => m.test(text) || (e.modell ? m.test(e.modell) : false))) return true;
  const integration = e.integration ?? "";
  return (
    (PLATTINTEGRATIONER.includes(integration) || SPARNINGSINTEGRATIONER.includes(integration)) &&
    TAB_ELLER_PAD.test(text)
  );
}

/**
 * b20: varför en enhet hoppades över som personlig, i ord, för navets lokala status (sudo
 * batkoll-nav status och loggen i Home Assistant). Lämnar aldrig navet. Null om den inte är det.
 */
export function personligKlass(e: Personuppgift): string | null {
  if (!arPersonlig(e)) return null;
  const sort = personligModell(e);
  if (sort) return sort;
  const integration = e.integration ?? "";
  if (integration === "mobile_app" || integration === "ios") return "mobilappen";
  if (SPARNINGSINTEGRATIONER.includes(integration)) return "spårning";
  if (e.sparas && NATVERKSINTEGRATIONER.includes(integration)) return "klient på nätet";
  return "personlig";
}

/**
 * b18: en fast skärm: en surfplatta, en skärm i kioskläge eller en pekskärm. Aldrig en telefon,
 * en klocka eller en dator, och aldrig en surfplatta från en integration för spårning.
 */
export function arFastSkarm(e: Personuppgift): boolean {
  const integration = e.integration ?? "";
  if (arPersonligModell(e)) return false;
  if (SPARNINGSINTEGRATIONER.includes(integration)) return false;
  if (KIOSKINTEGRATIONER.includes(integration)) return true;
  if (integration === "control4" && CONTROL4_SKARM.test(e.modell ?? "")) return true;
  if (arSurfplatta(e)) return true;
  const text = textFor(e);
  return text !== "" && PEKSKARMAR.some((m) => m.test(text));
}

/** Är enheten en persons enhet, som aldrig blir ett fynd? */
export function arPersonlig(e: Personuppgift): boolean {
  if (arFastSkarm(e)) return false;
  const integration = e.integration ?? "";
  if (PERSONLIGA_INTEGRATIONER.includes(integration)) return true;
  if (e.sparas && NATVERKSINTEGRATIONER.includes(integration)) return true;
  return arPersonligModell(e) || (SPARNINGSINTEGRATIONER.includes(integration) && arSurfplatta(e));
}

/** Ett fynd från Home Assistant eller Homey som är en persons enhet. */
export function arPersonligtFynd(f: {
  ha?: { integration: string; tillverkare: string | null; modell: string | null; klass?: string };
  homey?: { klass: string; app: string | null; drivrutin: string | null };
}): boolean {
  if (f.ha) return f.ha.klass === "fast_skarm" ? arPersonligModell(f.ha) : arPersonlig(f.ha);
  if (f.homey) {
    if (["phone", "watch", "laptop", "computer"].includes(f.homey.klass)) return true;
    return arPersonlig({ tillverkare: f.homey.app, modell: f.homey.drivrutin });
  }
  return false;
}

/** b18: ett fynd från Home Assistant som är en fast skärm (navet säger det, eller modellen). */
export function arFastSkarmFynd(f: {
  ha?: { integration: string; tillverkare: string | null; modell: string | null; klass?: string };
}): boolean {
  if (!f.ha) return false;
  return f.ha.klass === "fast_skarm" || arFastSkarm(f.ha);
}
